import '@adornis/chemistry/elements/components/x-flex';
import { html, nothing } from 'lit';
import '../client/components/fps-fonts';
import { AICheckerAnswer } from './questions';

export class Result {
  generateResult = (answers: AICheckerAnswer[]) => {
    const nichtAnwendbar =
      answers.includes(AICheckerAnswer.question_1_2) || answers.includes(AICheckerAnswer.question_3_2);
    return html`
      <x-flex space="lg">
        <x-flex space="lg">
          <fps-h3> Sie haben erfolgreich den FPS-KI-Check absolviert. </fps-h3>
          ${nichtAnwendbar ? this.nichtAnwendbarText(answers) : this.selbstTestText(answers)}
        </x-flex>
        ${nichtAnwendbar
          ? nothing
          : html`
              ${this.isHighRisk(answers) ? this.highRiskText(answers) : this.mediumRiskText(answers)}
              ${this.isGeneralPurpose(answers)
                ? this.isSystemicRisk(answers)
                  ? this.systemicRiskText()
                  : this.noSystemicRiskText(answers)
                : nothing}
            `}
        ${this.disclaimer()}
      </x-flex>
    `;
  };

  nichtAnwendbarText = (answers: AICheckerAnswer[]) => {
    return html`
      <fps-text>
        Die KI-Verordnung ist voraussichtlich nicht anwendbar. Beachten Sie jedoch die weiteren gesetzlichen Regelungen
        aus den Bereichen IT und Datenschutz, wie z.B. die Datenschutz-Grundverordnung, den Digital Services Act, den
        Data-Act, den Cyber-Resiliance Act oder die NIS-2-Richtlinie, die bereits in Kraft getreten sind, oder demnächst
        in Kraft treten.
      </fps-text>
    `;
  };

  selbstTestText = (answers: AICheckerAnswer[]) => {
    if (answers.includes(AICheckerAnswer.question_1_1) && answers.includes(AICheckerAnswer.question_3_1))
      return html`
        <fps-text>
          Folgende wesentlichen Pflichten bestehen für Sie als
          ${this.isProvider(answers) ? 'Anbieter/Softwarehersteller' : 'Nutzer'}
          ${this.isHighRisk(answers) ? 'eines Hochrisiko-KI-Systems' : 'einer KI ohne hohes Risiko'}
          ${this.introSystemRiskText(answers)} entsprechend der KI-Verordnung:
        </fps-text>
      `;
    return nothing;
  };

  introSystemRiskText = (answers: AICheckerAnswer[]) => {
    if (!answers.includes(AICheckerAnswer.QUESTION_8_1)) return '';
    const hasSystemicRisk =
      answers.includes(AICheckerAnswer.QUESTION_9_1) || answers.includes(AICheckerAnswer.QUESTION_9_2);
    let text = 'unter Nutzung einer general-purpose AI';
    if (hasSystemicRisk) text += ' mit ';
    else text += ' ohne ';
    text += 'systemische';
    if (hasSystemicRisk) text += 'm ';
    else text += 's ';
    text += 'Risiko';

    return text;
  };

  highRiskText = (answers: AICheckerAnswer[]) => {
    return html`
      <x-flex space="md">
        <fps-h3> HOCHRISIKO-KI </fps-h3>
        <x-flex space="md">
          ${this.isProvider(answers)
            ? html`
                <ul>
                  <li>
                    <fps-text>
                      Risikomanagementsystem: Es ist ein Risikomanagementsystem einzurichten und während des gesamten
                      Anwendungszeitraums aufrechtzuerhalten. Die ergriffenen Risikomanagement-Maßnahmen sind zudem
                      entsprechend zu dokumentieren.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Data Governance: Hochrisiko-KI darf nur mit Daten trainiert und betrieben werden, die strenge
                      Anforderungen an die Datenqualität erfüllen.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Technische Dokumentation: Vor dem Inverkehrbringen oder der Inbetriebnahme eines
                      Hochrisiko-KI-Systems müssen dessen technische Unterlagen dokumentiert und auf dem neusten Stand
                      gehalten werden.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Aufzeichnungen („Logs“): Abläufe und Entscheidungen der KI müssen von dem System automatisiert
                      protokoliert werden („Logs“).
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Transparenz: Es muss nachvollziehbar dargelegt werden, wie die KI zu ihren Ergebnissen gelangt.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Menschliche Überwachung: Während des laufenden Betriebs muss ein Mensch die Möglichkeit haben, das
                      KI-System zu überwachen und ggf. zu stoppen.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Genauigkeit, Robustheit und Cyber-Security: Hochrisiko-KI-Systeme sind so zu konzipieren und zu
                      entwickeln, dass sie während des gesamten Anwendungszeitraums ein angemessenes Maß an Genauigkeit,
                      Robustheit und Cyber-Security erreichen.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Qualitätsmanagementsystem: Anbieter von Hochrisiko-KI-Systemen richten ein
                      Qualitätsmanagementsystem ein, das die Einhaltung der KI-Verordnung gewährleistet. Dieses muss
                      u.a. folgende Aspekte umfassen: Ein Konzept zur Einhaltung der Regulierungsvorschriften;
                      Untersuchungs-, Test- und Validierungsverfahren; Systeme und Verfahren für das Datenmanagement und
                      Verfahren zur Meldung von Vorfällen und Fehlfunktionen.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Automatisch erzeugte Protokolle („Logs“) des Hochrisiko-KI-Systems müssen aufbewahrt werden.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Korrekturmaßnahmen: Anbieter von Hochrisiko-KI-Systemen müssen die erforderlichen
                      Korrekturmaßnahmen ergreifen, wenn das Hochrisiko-KI-System die Anforderungen der KI-Verordnung
                      nicht erfüllt.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      CE-Kennzeichnung / Konformitätsbewertung: Anbieter von Hochrisiko-KI-Systemen müssen eine
                      CE-Kennzeichnung für ihr System einholen oder eine Konformitätsbewertung vornehmen (lassen).
                    </fps-text>
                  </li>
                </ul>
              `
            : nothing}
          ${this.isUser(answers)
            ? html`
                <ul>
                  <li>
                    <fps-text>
                      Nutzung des System gemäß Anweisungen des Anbieters: Nutzer stellen sicher, dass das System gemäß
                      den Nutzungsanweisungen des Anbieters genutzt – und von qualifiziertem Personal überwacht wird.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Kontrolle der Eingabedaten: Nutzer stellen sicher, dass die unter ihrer Kontrolle befindlichen
                      Eingabedaten im Hinblick auf den beabsichtigten Zweck des Hochrisiko-KI-Systems relevant und
                      ausreichend repräsentativ sind.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Information von Anbieter und Aufsicht: Notwendige Meldungen der Nutzer über Zwischenfälle und
                      Risiken für natürliche Personen erfolgen an den Provider und die zuständige Aufsichtsbehörde.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Aufbewahrung Logs: Nutzer haben die von dem System erstellten Logs mindestens 6 Monate
                      aufzubewahren.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Informationspflicht: Bei Systemen, die Entscheidungen in Bezug auf natürliche Personen treffen
                      oder an solchen Entscheidungen mitwirken, informieren die Nutzer die Betroffenen darüber, dass sie
                      dem Einsatz eines KI-Systems mit hohem Risiko ausgesetzt sind.
                    </fps-text>
                  </li>
                  <li>
                    <fps-text>
                      Grundrechtliche Folgenabschätzung: Bei KI-Systemen, die (i.) von öffentlichen Einrichtungen, oder
                      (ii.) privaten Einrichtungen, die öffentliche Dienstleistungen wahrnehmen und in besonders
                      sensiblen Bereichen eingesetzt werden, bewerten Nutzer vor Inbetriebnahme des Systems, welche
                      Auswirkungen der Einsatz auf die Grundrechte der Betroffenen haben kann.
                    </fps-text>
                  </li>
                </ul>
                <fps-text>
                  Daneben müssen die gesetzlichen Bestimmungen zu sonstigen betroffenen Rechtsbereichen wie zum Beispiel
                  dem Datenschutz, der Datensicherheit und dem Urheberrecht gewahrt werden.
                </fps-text>
              `
            : nothing}
        </x-flex>
        ${this.isProvider(answers)
          ? html`
              <fps-text>
                Daneben müssen die gesetzlichen Bestimmungen zu sonstigen betroffenen Rechtsbereichen wie zum Beispiel
                dem Datenschutz, der Datensicherheit und dem Urheberrecht gewahrt werden.
              </fps-text>
            `
          : nothing}
      </x-flex>
    `;
  };

  mediumRiskText = (answers: AICheckerAnswer[]) => {
    return html`
      <x-flex space="md">
        <fps-h3> KI ohne hohes Risiko </fps-h3>
        <ul>
          <li>
            <fps-text> Kennzeichnungs-/ Informationspflicht: </fps-text>
            <ul>
              <li>
                <fps-text>
                  KI, die mit Menschen agiert und nicht offensichtlich als KI erkennbar ist, muss als solche kenntlich
                  gemacht werden.
                </fps-text>
              </li>
              <li>
                <fps-text>
                  Bei Emotionserkennungssystemen oder Systemen zur biometrischen Kategorisierung müssen die Betroffenen
                  in jedem Fall informiert werden.
                </fps-text>
              </li>
              <li>
                <fps-text>
                  Ausgaben eines KI-Systems in Form von synthetischen Audio-, Bild-, Video- oder Textinhalten müssen in
                  einem maschinenlesbaren Format gekennzeichnet werden.
                </fps-text>
              </li>
              <li>
                <fps-text>
                  Deep Fakes (manipulierte Bild-, Audio- oder Videoinhalte - sofern nicht offenkundig z.B. als Teil von
                  Literatur oder Kunst) müssen gesondert als solche gekennzeichnet werden.
                </fps-text>
              </li>
              <li>
                <fps-text>
                  Themen von öffentlichem Interesse, die durch KI generiert wurden, müssen gekennzeichnet werden, sofern
                  sie nicht von einer natürlichen Person geprüft wurden und eine natürliche bzw. juristische Person für
                  die Publikation redaktionelle Verantwortung trägt.
                </fps-text>
              </li>
            </ul>
          </li>
        </ul>
        <fps-text>
          Daneben müssen die gesetzlichen Bestimmungen zu sonstigen betroffenen Rechtsbereichen wie zum Beispiel dem
          Datenschutz, der Datensicherheit und dem Urheberrecht gewahrt werden.
        </fps-text>
      </x-flex>
    `;
  };

  noSystemicRiskText = (answers: AICheckerAnswer[]) => {
    return html`
      <x-flex space="md">
        <fps-h3> General-purpose AI ohne systemisches Risiko: </fps-h3>
        <ul>
          <li>
            <fps-text>
              Dokumentation: Führen einer technischen Dokumentation des Modells, die sowohl das Training als auch die
              Bewertungsergebnisse des Modells dokumentiert.
            </fps-text>
          </li>
          <li>
            <fps-text>
              Informationen für nachgelagerte Anbieter: Erarbeitung von Unterlagen und Anweisungen für nachgelagerte
              Anbieter, die das general-purpose AI-Modell in ihr eigenes KI-System integrieren wollen. Die erstellten
              Unterlagen müssen die nachgelagerten Anbieter in die Lage versetzen, die gesetzlichen Vorgaben einzuhalten
              (z.B. Herstellung der Transparenz von Entscheidungswegen).
            </fps-text>
          </li>
          <li>
            <fps-text>
              Urheberrecht: Etablierung von Regeln zur Einhaltung des in der EU geltenden Urheberrechts unter
              Berücksichtigung der Bestimmungen zum Text- und Data-Mining.
            </fps-text>
          </li>
          <li>
            <fps-text>
              Transparenz: Veröffentlichung einer hinreichend detaillierten Zusammenfassung der für das Training des
              general-purpose AI-Modells verwendeten Inhalte.
            </fps-text>
          </li>
        </ul>
        <fps-text>
          <u>Hinweis</u>: Der Pflichtenkatalog kann sich um Anbieterpflichten reduzieren, wenn Sie ein general-purpose
          AI-Modell nachgelagerten Anbietern (bspw. über eine Schnittstelle) zur Verfügung stellen und diese Pflichten
          übernehmen.
        </fps-text>
      </x-flex>
    `;
  };

  systemicRiskText = () => {
    return html`
      <x-flex space="md">
        <fps-h3> General-purpose AI mit systemischem Risiko: </fps-h3>
        <ul>
          <li>
            <fps-text>
              Dokumentation: Führen einer technischen Dokumentation des Modells, die sowohl das Training als auch die
              Bewertungsergebnisse des Modells dokumentiert.
            </fps-text>
          </li>
          <li>
            <fps-text>
              Informationen für nachgelagerte Anbieter: Erarbeitung von Unterlagen und Anweisungen für nachgelagerte
              Anbieter, die das general-purpose AI-Modell in ihr eigenes KI-System integrieren wollen. Die erstellten
              Unterlagen müssen die nachgelagerten Anbieter in die Lage versetzen, die gesetzlichen Vorgaben einzuhalten
              (z.B. Herstellung der Transparenz von Entscheidungswegen).
            </fps-text>
          </li>
          <li>
            <fps-text>
              Urheberrecht: Etablierung von Regeln zur Einhaltung des in der EU geltenden Urheberrechts unter
              Berücksichtigung der Bestimmungen zum Text- und Data-Mining.
            </fps-text>
          </li>
          <li>
            <fps-text>
              Transparenz: Veröffentlichung einer hinreichend detaillierten Zusammenfassung der für das Training des
              general-purpose AI-Modells verwendeten Inhalte.
            </fps-text>
          </li>
          <li>
            <fps-text>
              Evaluierung: Durchführung von Modell-Evaluierungen, einschließlich der Durchführung und Dokumentation
              kontradiktorischer Tests zur Ermittlung und Minderung systemischer Risiken.
            </fps-text>
          </li>
          <li><fps-text> Bewertung und Abschwächung möglicher Risiken. </fps-text></li>
          <li>
            <fps-text>
              Verfolgung, Dokumentation und Meldung von Vorfällen und möglicher Abhilfemaßnahmen an das „KI-Büro“ und
              die zuständigen nationalen Behörden.
            </fps-text>
          </li>
        </ul>
        <fps-text>
          <u>Hinweis</u>: Der Pflichtenkatalog kann sich um Anbieterpflichten reduzieren, wenn Sie ein general-purpose
          AI-Modell nachgelagerten Anbietern (bspw. über eine Schnittstelle) zur Verfügung stellen und diese Pflichten
          übernehmen.
        </fps-text>
      </x-flex>
    `;
  };

  // Helper functions
  isUser = (answers: AICheckerAnswer[]) => {
    return answers.includes(AICheckerAnswer.question_2_1);
  };

  isProvider = (answers: AICheckerAnswer[]) => {
    return answers.includes(AICheckerAnswer.question_2_2);
  };

  isHighRisk = (answers: AICheckerAnswer[]) => {
    return answers.includes(AICheckerAnswer.question_4_1) || answers.includes(AICheckerAnswer.QUESTION_6_5);
  };

  isGeneralPurpose = (answers: AICheckerAnswer[]) => {
    return answers.includes(AICheckerAnswer.QUESTION_8_1);
  };

  isSystemicRisk = (answers: AICheckerAnswer[]) => {
    return answers.includes(AICheckerAnswer.QUESTION_9_1) && answers.includes(AICheckerAnswer.question_2_2);
  };

  disclaimer() {
    return html`
      <fps-subtext italic tone="subtle">
        Der FPS KI-Check ersetzt keine individuelle Rechtsberatung, er erhebt keinen den Anspruch auf Vollständigkeit.
        <br />
        <br />
        Sollten Sie KI-Systeme im Rahmen militärischer Zwecke oder zu Zwecken der Strafverfolgung bzw. in der Justiz
        einsetzen oder anbieten, kann das Vorstehende Ergebnis auf Sie im Einzelfall nicht zutreffen. Dasselbe gilt für
        Hochrisiko-KI-Systeme, die Sicherheitskomponenten von Produkten oder Systemen in den folgenden Bereichen sind:
        Sicherheit der Luftfahrt und von Flugzeugen, Sicherheit und Kontrolle von Land- und Wasserfahrzeugen,
        einschließlich Autos, Motorräder, Eisenbahnen und Schiffen. <br />
        <br />
        Bitte beachten Sie ferner, dass gewisse KI-Systeme gänzlich verboten sind. Hierunter fallen zum Beispiel
        diskriminierende KI-Systeme oder KI-Systeme, die ein Social-Scoring vornehmen (siehe Art.5 der KI-VO).
      </fps-subtext>
    `;
  }
}
